import React, { Fragment, useEffect, useState } from 'react';

import IconUser from '@/assets/user.svg?react';

import {
  useClaimPanelInfoMutation,
  useClaimPanelMutation,
} from '@/api/claimPanel/getters';
import { LoginData } from '@/api/types/claimPanel';
import IconEye from '@/assets/eye.svg?react';
import IconEyeClose from '@/assets/eye-close.svg?react';
import Badge from '@/components/Badge';
import SummaryWrapper from '@/components/layouts/SummaryWrapper';
import Button, {
  ButtonLoading,
  ButtonRightArrow,
} from '@/components/ui/Button';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from '@/components/ui/Form';
import { Hint } from '@/components/ui/Hint';
import { Input } from '@/components/ui/Input';
import { validatePesel } from '@/features/v2/summaryForms/Validate';
import cn from '@/utils/cn';

import { useLoginContext } from '../lib/login';
import { useAutoAnimate } from '@formkit/auto-animate/react';
import { Transition } from '@headlessui/react';
import { useLingui } from '@lingui/react';
import { Form, FormProvider, useForm } from 'react-hook-form';
import { useParams, useSearchParams } from 'react-router-dom';

const Login = ({ show }: { show: boolean }) => {
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [parent] = useAutoAnimate({
    duration: 200,
  });

  const { i18n } = useLingui();

  const methods = useForm({
    mode: 'onBlur',
  });

  const { handleLogin, loginError, setLoginError } = useLoginContext();
  const claimPanelMutation = useClaimPanelMutation();
  const claimPanelInfoMutation = useClaimPanelInfoMutation();

  useEffect(() => {
    if (show && searchParams.has('case')) {
      const next = new URLSearchParams(searchParams);
      next.delete('case');
      setSearchParams(next, { replace: true });
    }
  }, [show, searchParams, setSearchParams]);

  const submit = async ({ data }: { data: LoginData }) => {
    const hash = params?.id;
    if (!data) return;
    if (!hash) {
      const res = await claimPanelInfoMutation.mutateAsync({ data });
      const claim = await res.json();
      if (res.ok) {
        setSearchParams({ case: data.caseId });
        handleLogin(claim);

        methods.reset({
          pesel: '',
          caseId: '',
        });
        return;
      }

      setLoginError(claim);
      return;
    }

    const res = await claimPanelMutation.mutateAsync({ data, hash });
    const claim = await res.json();
    if (res.ok) {
      setSearchParams({ case: data.caseId });
      handleLogin(claim);

      methods.reset({
        pesel: '',
        caseId: '',
      });
      return;
    }

    setLoginError(claim);
  };

  const [focused, setFocused] = useState<string>('');

  const [passwordShow, setPasswordShow] = useState(false);

  return (
    <Transition
      as={Fragment}
      enter="transition-all ease-out duration-300 delay-300"
      enterFrom="opacity-0 translate-y-10"
      enterTo="opacity-100 translate-y-0"
      leave="transition-all ease-in duration-200"
      leaveFrom="opacity-100 translate-y-0"
      leaveTo="opacity-0 translate-y-10"
      show={show}
      appear
      unmount={false}
    >
      <div className="absolute inset-x-0 flex items-center justify-center px-4 lg:px-0">
        <SummaryWrapper className="w-full gap-y-3 md:w-[440px] lg:flex lg:flex-col lg:gap-y-4 lg:p-6">
          <h2 className="h3 flex w-full gap-2">
            {i18n.t('Sprawdź status swojej sprawy')}
          </h2>
          <div
            className="flex flex-col gap-3 rounded-md bg-gray-200 p-3.5 lg:p-6"
            ref={parent}
          >
            <FormProvider {...methods}>
              <Form onSubmit={submit}>
                <FormField
                  control={methods.control}
                  name="caseId"
                  defaultValue=""
                  rules={{
                    validate: (value: string) => {
                      if (value.length > 4) return true;
                      return i18n.t('Wprowadź poprawny numer sprawy');
                    },
                  }}
                  render={({
                    field: { onChange, onBlur, ...field },
                    fieldState: { error },
                  }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2">
                        <IconUser width={16} className="text-blue-600" />
                        {i18n.t('Dane do logowania')}
                      </FormLabel>
                      <FormControl>
                        <Input
                          label={i18n.t('Numer sprawy')}
                          error={error?.message}
                          inputMode="numeric"
                          onChange={(e) => {
                            if (!/^[0-9]*$/.test(e.target.value)) return;
                            onChange(e);
                            setLoginError(false);
                            if (error) {
                              methods.trigger(field.name);
                            }
                          }}
                          successNeutral={
                            focused !== field.name && !error && field.value
                          }
                          onBlur={() => {
                            onBlur();
                            setFocused('');
                          }}
                          onFocus={() => setFocused(field.name)}
                          {...field}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <Hint
                  variant="default"
                  color="blue"
                  icon="bulb"
                  className="mt-3"
                >
                  {i18n.t(
                    'Numer sprawy widnieje w nazwie umowy, która została podpisana z Helpfind.'
                  )}
                </Hint>
                <FormField
                  control={methods.control}
                  name="pesel"
                  rules={{
                    validate: (value: string) => {
                      return validatePesel(value);
                    },
                  }}
                  render={({
                    field: { onChange, onBlur, ...field },
                    fieldState: { error },
                  }) => (
                    <FormItem className="mt-3">
                      <FormControl>
                        <Input
                          label={i18n.t('PESEL powoda')}
                          error={error?.message}
                          type={passwordShow ? 'text' : 'password'}
                          rightSection={
                            <div
                              onClick={(e) => {
                                e.stopPropagation();
                                setPasswordShow(!passwordShow);
                              }}
                            >
                              {passwordShow ? (
                                <IconEyeClose className="size-[22px] cursor-pointer text-dark-gray-300 duration-300 hover:text-dark-gray-200" />
                              ) : (
                                <IconEye className="size-[22px] cursor-pointer text-dark-gray-300 duration-300 hover:text-dark-gray-200" />
                              )}
                            </div>
                          }
                          autoComplete="off"
                          inputMode="numeric"
                          onChange={(e) => {
                            if (!/^[0-9]*$/.test(e.target.value)) return;
                            if (
                              field.value?.length >= 11 &&
                              e.target.value?.length > field.value?.length
                            )
                              return;
                            onChange(e);
                            setLoginError(false);
                            if (error) {
                              methods.trigger(field.name);
                            }
                          }}
                          successNeutral={
                            focused !== field.name && !error && field.value
                          }
                          onBlur={() => {
                            onBlur();
                            setFocused('');
                          }}
                          onFocus={() => setFocused(field.name)}
                          {...field}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                {loginError && (
                  <Hint
                    variant="default"
                    color="red"
                    icon="info"
                    className="mt-3"
                  >
                    {i18n.t('Niepoprawny numer sprawy lub PESEL.')}
                  </Hint>
                )}
                <Button
                  type={'submit'}
                  className={cn(
                    'mt-5 w-full transition-colors duration-200 ease-in-out lg:w-full',
                    claimPanelMutation.isPending && 'pointer-events-none'
                  )}
                  disabled={loginError || !methods.formState.isValid}
                  loading={claimPanelMutation.isPending}
                >
                  {i18n.t('Zaloguj się')}
                  <ButtonRightArrow />
                  <ButtonLoading />
                </Button>
              </Form>
            </FormProvider>
          </div>
          <Badge variant="padlock" className="bg-gray-200" />
        </SummaryWrapper>
      </div>
    </Transition>
  );
};

export default Login;
